/**
 * SYNCED FROM SERVER — do not edit by hand.
 *
 * Source: `server/src/api/<domain>/<domain>.contract.ts` (or the
 * shared bases under `server/src/api/common/contracts/`).
 *
 * Regenerate: `cd api-client && bun run sync:contracts`.
 *
 * The server is the source of truth for contract definitions. This
 * file exists so the web SPA can import zod schemas + oRPC contract
 * types from `@event-manager/api-client/contracts/<domain>.contract`
 * without reaching into the server workspace.
 */

import { oc } from "@orpc/contract";
import * as z from "zod";
import {
	OrgPlanSchema,
	PlanPriceSchema,
	PricingMarketSchema,
} from "../common/org-plan";

// ============================================================================
// Schemas
// ============================================================================

export const SubscriptionStatusSchema = z.enum([
	"none",
	"active",
	"past_due",
	"canceled",
	"trialing",
]);

export type SubscriptionStatus = z.infer<typeof SubscriptionStatusSchema>;

export const BillingIntervalSchema = z.enum(["monthly", "yearly"]);

export type BillingInterval = z.infer<typeof BillingIntervalSchema>;

export const PaymentMethodSchema = z.object({
	brand: z.string(),
	last4: z.string(),
});

export type PaymentMethod = z.infer<typeof PaymentMethodSchema>;

/**
 * Provider id surfaced to the frontend so the UI can render provider-specific
 * affordances (e.g., "Manage in Stripe Customer Portal" vs Paymob's
 * "Contact support to change plan"). null = unset → org has not checked
 * out yet, frontend renders the pre-checkout pricing view.
 */
export const PaymentProviderIdSchema = z.enum([
	"stripe",
	"paymob_eg",
	"paymob_sa",
]);

export type PaymentProviderId = z.infer<typeof PaymentProviderIdSchema>;

export const BillingStatusSchema = z.object({
	/** True when AT LEAST ONE payment provider is configured server-side. */
	billingConfigured: z.boolean(),
	/**
	 * The provider currently routing this org. Null until first checkout —
	 * at which point the routing service locks the org to one provider for
	 * all future operations. Drives UI affordances.
	 */
	provider: PaymentProviderIdSchema.nullable(),
	/**
	 * True when the active provider exposes a hosted self-serve portal
	 * (Stripe yes, Paymob no). When false the UI hides the portal button
	 * and surfaces a contact-support / built-in cancel flow instead.
	 */
	portalAvailable: z.boolean(),
	plan: OrgPlanSchema,
	subscriptionStatus: SubscriptionStatusSchema,
	/**
	 * Start of the current billing period (Stripe-synced); null on free tier.
	 * Optional so responses from a pre-rollout server still validate.
	 */
	currentPeriodStart: z.string().datetime().nullable().optional(),
	currentPeriodEnd: z.string().datetime().nullable(),
	billingInterval: BillingIntervalSchema.nullable(),
	cancelAtPeriodEnd: z.boolean(),
	paymentMethod: PaymentMethodSchema.nullable(),
	/** True when the org has a provider-side customer record (any provider). */
	hasProviderCustomer: z.boolean(),
	/** The org's pricing market, from its country; null while no country is set. */
	market: PricingMarketSchema.nullable(),
});

export type BillingStatus = z.infer<typeof BillingStatusSchema>;

export const PlanPricingSchema = z.object({
	plan: OrgPlanSchema,
	displayName: z.string(),
	prices: z.record(PricingMarketSchema, PlanPriceSchema),
	features: z.array(z.string()),
});

export type PlanPricing = z.infer<typeof PlanPricingSchema>;

export const PricingResponseSchema = z.object({
	billingConfigured: z.boolean(),
	plans: z.array(PlanPricingSchema),
});

export type PricingResponse = z.infer<typeof PricingResponseSchema>;

// ----------------------------------------------------------------------------
// Invoices (billing history)
// ----------------------------------------------------------------------------

/**
 * Stripe invoice status -- see
 * https://docs.stripe.com/api/invoices/object#invoice_object-status
 * (`draft | open | paid | void | uncollectible`).
 */
export const InvoiceStatusSchema = z.enum([
	"draft",
	"open",
	"paid",
	"void",
	"uncollectible",
]);

export type InvoiceStatus = z.infer<typeof InvoiceStatusSchema>;

export const InvoiceSchema = z.object({
	id: z.string(),
	/** Stripe-assigned invoice number; null on drafts. */
	number: z.string().nullable(),
	/** Amount paid in the smallest currency unit (e.g. cents). */
	amountPaid: z.number().int(),
	/** Total invoice amount in the smallest currency unit. */
	amountDue: z.number().int(),
	/** ISO 4217 currency, lowercase (Stripe convention: e.g. "usd"). */
	currency: z.string(),
	status: InvoiceStatusSchema.nullable(),
	/** Invoice creation time (ISO 8601). */
	created: z.string().datetime(),
	/** Service period start (ISO 8601). */
	periodStart: z.string().datetime(),
	/** Service period end (ISO 8601). */
	periodEnd: z.string().datetime(),
	/** Stripe-hosted HTML invoice page; null for drafts/uncollectible. */
	hostedInvoiceUrl: z.string().nullable(),
	/** PDF download URL; null for drafts. */
	invoicePdf: z.string().nullable(),
	description: z.string().nullable(),
});

export type Invoice = z.infer<typeof InvoiceSchema>;

export const ListInvoicesResponseSchema = z.object({
	billingConfigured: z.boolean(),
	hasProviderCustomer: z.boolean(),
	invoices: z.array(InvoiceSchema),
});

export type ListInvoicesResponse = z.infer<typeof ListInvoicesResponseSchema>;

// ----------------------------------------------------------------------------
// Upcoming invoice preview (next-bill widget)
// ----------------------------------------------------------------------------

export const UpcomingInvoiceLineSchema = z.object({
	description: z.string().nullable(),
	amount: z.number().int(),
	quantity: z.number().int().nullable(),
	periodStart: z.string().datetime(),
	periodEnd: z.string().datetime(),
});

export type UpcomingInvoiceLine = z.infer<typeof UpcomingInvoiceLineSchema>;

export const UpcomingInvoiceSchema = z.object({
	/** Total amount that will be charged in the smallest currency unit. */
	amountDue: z.number().int(),
	/** Subtotal before tax/discounts. */
	subtotal: z.number().int(),
	/** ISO 4217 currency, lowercase. */
	currency: z.string(),
	/** Service period start (ISO 8601). */
	periodStart: z.string().datetime(),
	/** Service period end (ISO 8601). */
	periodEnd: z.string().datetime(),
	/** Estimated bill date (ISO 8601), null when Stripe doesn't compute one. */
	nextPaymentAttempt: z.string().datetime().nullable(),
	/** Per-line breakdown so the UI can show "Premium plan + overage". */
	lines: z.array(UpcomingInvoiceLineSchema),
});

export type UpcomingInvoice = z.infer<typeof UpcomingInvoiceSchema>;

export const UpcomingInvoiceResponseSchema = z.object({
	billingConfigured: z.boolean(),
	hasProviderCustomer: z.boolean(),
	/** Null when there's no upcoming invoice (e.g., subscription canceled). */
	upcoming: UpcomingInvoiceSchema.nullable(),
});

export type UpcomingInvoiceResponse = z.infer<
	typeof UpcomingInvoiceResponseSchema
>;

// ----------------------------------------------------------------------------
// Usage (seats / messages / storage)
// ----------------------------------------------------------------------------

export const UsageMetricSchema = z.object({
	used: z.number().int().nonnegative(),
	limit: z.number().int().nonnegative(),
});

export type UsageMetric = z.infer<typeof UsageMetricSchema>;

export const StorageUsageSchema = z.object({
	usedBytes: z.number().nonnegative(),
	limitBytes: z.number().nonnegative(),
});

export type StorageUsage = z.infer<typeof StorageUsageSchema>;

export const UsageSchema = z.object({
	plan: OrgPlanSchema,
	/** Active staff seats (users with email set) vs effective seat limit; `null` = unlimited. */
	seats: UsageMetricSchema.extend({
		limit: z.number().int().nonnegative().nullable(),
	}),
	/** AI messages used in the current calendar month (UTC) vs limit. */
	messages: UsageMetricSchema.extend({
		periodStart: z.string().datetime(),
		periodEnd: z.string().datetime(),
	}),
	/** Sum of RAG document bytes vs effective storage limit. */
	storage: StorageUsageSchema,
});

export type Usage = z.infer<typeof UsageSchema>;

// ============================================================================
// Contract
// ============================================================================

export const billingContract = oc.router({
	/**
	 * Get pricing information (public -- no auth required).
	 * Returns plan details with prices and whether Stripe is configured.
	 */
	getPricing: oc
		.route({ method: "GET", path: "/api/billing/pricing" })
		.output(PricingResponseSchema),

	/**
	 * Get billing status for the current user's organization.
	 * Requires authentication.
	 */
	getStatus: oc
		.route({ method: "GET", path: "/api/billing/status" })
		.output(BillingStatusSchema)
		.errors({
			UNAUTHORIZED: { message: "Authentication required" },
		}),

	/**
	 * Subscription mutations -- `createCheckoutSession`, `createPortalSession`,
	 * `cancelSubscription` -- moved to the Better Auth Stripe plugin.
	 *
	 * Frontend callers use `authClient.subscription.{upgrade,billingPortal,cancel}`
	 * directly. The plugin handles webhook lifecycle; the
	 * `OrganizationPlanChangedHandler` listens to the bridged event and keeps
	 * the per-org OpenRouter sub-key limit in sync.
	 *
	 * Read endpoints below remain on this contract because they aggregate
	 * application-side data (plan-display copy, our usage table, our cost-cap
	 * config) that the plugin does not expose.
	 */

	/**
	 * Eagerly sync Stripe data after checkout success.
	 *
	 * Per t3dotgg/stripe-recommendations: call syncStripeData immediately
	 * when the user returns from checkout, BEFORE webhooks arrive.
	 * This prevents the race condition where the user sees stale data
	 * because the webhook hasn't been processed yet.
	 *
	 * Returns the updated billing status.
	 */
	syncAfterCheckout: oc
		.route({ method: "POST", path: "/api/billing/sync" })
		.output(BillingStatusSchema)
		.errors({
			UNAUTHORIZED: { message: "Authentication required" },
			PRECONDITION_FAILED: {
				message: "No Stripe customer found for this organization",
			},
		}),

	/**
	 * Resolve a Stripe Checkout session by id and sync the underlying
	 * org's subscription state.
	 *
	 * Used by `/onboarding/success` as a session-cookie-independent
	 * fallback for the rare path where the user lost their session
	 * mid-checkout (cleared cookies, different browser, etc.). The
	 * normal path (`syncAfterCheckout`) reads the customer via
	 * `getCurrentUser().orgId`; this one reads the customer from the
	 * Stripe Checkout Session directly via the supplied `sessionId`.
	 *
	 * Auth: requires a session, but does NOT require the calling
	 * user's `orgId` to match the Checkout session's customer (a user
	 * who paid as anonymous + later signed up may legitimately not
	 * have orgId yet on this device). The Stripe-side `session.customer`
	 * + the resulting `subscription` are the source of truth.
	 *
	 * Returns the same `BillingStatus` shape as `syncAfterCheckout`.
	 */
	/**
	 * List invoices for the current org's Stripe customer (billing history).
	 * Requires org admin/owner role, or global SUPERUSER. Returns an empty
	 * array if the org has no stripeCustomerId yet (i.e. never checked out).
	 */
	listInvoices: oc
		.route({ method: "GET", path: "/api/billing/invoices" })
		.output(ListInvoicesResponseSchema)
		.errors({
			UNAUTHORIZED: { message: "Authentication required" },
			FORBIDDEN: {
				message: "Org admin role required",
				data: z.object({ requiredRole: z.string().nullish() }),
			},
			PRECONDITION_FAILED: { message: "Stripe is not configured" },
		}),

	/**
	 * Quota usage (seats, messages, storage) for the current org.
	 * Requires org admin/owner role, or global SUPERUSER. Limits resolve
	 * via `resolveOrgLimits()` so per-org overrides win over plan defaults.
	 */
	getUsage: oc
		.route({ method: "GET", path: "/api/billing/usage" })
		.output(UsageSchema)
		.errors({
			UNAUTHORIZED: { message: "Authentication required" },
			FORBIDDEN: {
				message: "Org admin role required",
				data: z.object({ requiredRole: z.string().nullish() }),
			},
		}),

	/**
	 * Preview the next invoice. Returns the Stripe-computed amount, period,
	 * and per-line breakdown so the UI can show "Your next bill: $X on Y".
	 *
	 * - Admin only.
	 * - Returns `{ upcoming: null }` when the org has no Stripe customer or
	 *   no upcoming invoice (canceled / paused subscription).
	 */
	getUpcomingInvoice: oc
		.route({ method: "GET", path: "/api/billing/upcoming-invoice" })
		.output(UpcomingInvoiceResponseSchema)
		.errors({
			UNAUTHORIZED: { message: "Authentication required" },
			FORBIDDEN: {
				message: "Org admin role required",
				data: z.object({ requiredRole: z.string().nullish() }),
			},
			PRECONDITION_FAILED: { message: "Stripe is not configured" },
		}),

	getCheckoutSessionStatus: oc
		.route({
			method: "POST",
			path: "/api/billing/checkout-session-status",
			summary: "Resolve a Stripe Checkout session and sync its org state",
		})
		.input(z.object({ sessionId: z.string().min(1) }))
		.output(BillingStatusSchema)
		.errors({
			UNAUTHORIZED: { message: "Authentication required" },
			NOT_FOUND: {
				message: "Stripe Checkout session not found",
				data: z.object({ id: z.union([z.number(), z.string()]) }),
			},
			PRECONDITION_FAILED: {
				message: "Stripe is not configured or session has no customer",
			},
		}),
});

export type BillingContract = typeof billingContract;
